import { logEmptySomeTrackInfo } from "../api/adminService";
import {
  ADVERTISING_NAME,
  defaultRadioInfoByType,
  NEWS_NAME,
  SYSTEM_TRACK_NAMES,
  WEATHER_NAME,
} from "./constants";

/**
 * @param {string} str
 */
export function trailingSlashEnd(str) {
  if (!str) {
    return "";
  }
  return str.endsWith("/") ? str.slice(0, -1) : str;
}

/**
 * @param {number} number
 * @param {number} length
 * @param {string} fillStr
 * @returns {string}
 */
export function getStringWithPadStart(number, length = 2, fillStr = "0") {
  return number.toString().padStart(length, fillStr);
}

/**
 * @param {number} time
 * @param {{
 *   showSeconds?: boolean;
 *   showMinutes?: boolean;
 *   showHours?: boolean;
 *   onlyMinutes?: boolean;
 *   delimiter?: string;
 * } | undefined} options
 */
export function getTimeFromMilliseconds(
  time,
  { showHours, showMinutes, showSeconds, onlyMinutes, delimiter = ":" } = {},
) {
  const ms = time % 1000;
  time = (time - ms) / 1000;
  const secs = time % 60;
  time = (time - secs) / 60;

  if (onlyMinutes) {
    return getStringWithPadStart(time);
  }

  const mins = time % 60;
  const hrs = (time - mins) / 60;

  const result = [];

  showHours && result.push(getStringWithPadStart(hrs));
  showMinutes && result.push(getStringWithPadStart(mins));
  showSeconds && result.push(getStringWithPadStart(secs));

  return result.join(delimiter);
}

/**
 * @param {string} trackName
 * @return {boolean}
 */
export function isSystemTrack(trackName) {
  return SYSTEM_TRACK_NAMES.includes(trackName);
}

/**
 * @param {string} type
 * @param {string} trackName
 */
export function getSystemTrackInfo(type, trackName) {
  const systemData = (() => {
    switch (trackName) {
      case ADVERTISING_NAME: {
        return {
          track: "Реклама",
          cover: {
            big: "/coverAds.png",
            small: "/coverAds.png",
          },
        };
      }
      case WEATHER_NAME: {
        return {
          track: "Погода",
          cover: {
            big: "/coverWeather.png",
            small: "/coverWeather.png",
          },
        };
      }
      case NEWS_NAME: {
        return {
          track: "Новости",
          cover: {
            big: "/coverNews.png",
            small: "/coverNews.png",
          },
        };
      }
      default: {
        return {
          cover: {
            big: "/coverMissing.png",
            small: "/coverMissing.png",
          },
        };
      }
    }
  })();

  return {
    ...defaultRadioInfoByType[type].aboutTrack,
    ...systemData,
    artist: "It-Radio",
  };
}

/**
 * @typedef {import("../redux/store/store").StoreApp} StoreApp
 * @typedef {keyof StoreApp['radioInfo']} radioType
 */

/**
 * @param {StoreApp['radioInfo'][radioType]['aboutTrack']} trackInfo
 */
export function checkTrackInfo(trackInfo) {
  const emptyKeys = getEmptyObjectKeys(trackInfo);

  if (emptyKeys.length) {
    logEmptySomeTrackInfo(trackInfo._id, {
      trackName: trackInfo.title,
      emptyKeys,
    });
  }
}

const ignoreEmptyKeys = ["userRating", "_id", "__typename"];

/**
 * @param {Record<string, any>} obj
 * @param {string} prefix
 * @returns {string[]}
 */
function getEmptyObjectKeys(obj, prefix = "") {
  const emptyFields = Object.entries(obj).reduce((res, [key, value]) => {
    if (!ignoreEmptyKeys.includes(key) && !value) {
      res.push(`${prefix}${key}`);
    } else if (value instanceof Object) {
      const emptyInnerKeys = getEmptyObjectKeys(value, `${prefix}${key}.`);
      res.push(...emptyInnerKeys);
    }

    return res;
  }, []);

  return emptyFields;
}

/**
 * Преобразует строку из snake_case в camelCase.
 * Примеры:
 * - "my_snake_case" → "mySnakeCase"
 *
 * @param {string} str - Строка, которую нужно преобразовать.
 * @returns {string} Строка в формате camelCase.
 */
export const toCamelCase = (str) => {
  return str.replace(/([-_][a-z])/gi, (group) =>
    group.toUpperCase().replace("_", ""),
  );
};

/**
 * Рекурсивно преобразует ключи объекта (или массива объектов) из snake_case  в camelCase.
 * Можно исключить определённые ключи из преобразования.
 * Примеры:
 * - { user_name: "John", address_info: { street_name: "Main St" } } → { userName: "John", addressInfo: { streetName: "Main St" } }
 * - { user_name: "John", zip_code: "12345" } (с excludeKeys = ["zip_code"]) → { userName: "John", zip_code: "12345" }
 *
 * @param {object | array} obj - Объект или массив, ключи которого нужно преобразовать.
 * @param {array} [excludeKeys=[]] - Массив ключей, которые не нужно преобразовывать.
 * @returns {object | array} Объект или массив с преобразованными ключами.
 */
export const transformKeysToCamelCase = (obj, excludeKeys = []) => {
  if (Array.isArray(obj)) {
    return obj.map((item) => transformKeysToCamelCase(item, excludeKeys));
  } else if (obj !== null && typeof obj === "object") {
    return Object.keys(obj).reduce((acc, key) => {
      const camelCaseKey = excludeKeys.includes(key) ? key : toCamelCase(key);
      acc[camelCaseKey] = transformKeysToCamelCase(obj[key], excludeKeys);
      return acc;
    }, {});
  }
  return obj;
};

/**
 * Рекурсивно заменяет "Track" на "Release" в ключах объекта (или массива объектов).
 * Примеры:
 * - { countPodcastTracks: 10, bestPodcastTracks: [...] } → { countPodcastReleases: 10, bestPodcastReleases: [...] }
 * - { podcastTracks: [{ id: 1, title: "Track 1" }] } → { podcastReleases: [{ id: 1, title: "Track 1" }] }
 *
 * @param {object | array} obj - Объект или массив, ключи которого нужно изменить.
 * @returns {object | array} Объект или массив с изменёнными ключами.
 */
export const replaceTrackWithRelease = (obj) => {
  if (Array.isArray(obj)) {
    return obj.map((item) => replaceTrackWithRelease(item));
  } else if (obj !== null && typeof obj === "object") {
    return Object.keys(obj).reduce((acc, key) => {
      const newKey = key.includes("Track")
        ? key.replace("Track", "Release")
        : key;
      acc[newKey] = replaceTrackWithRelease(obj[key]);
      return acc;
    }, {});
  }
  return obj;
};

/**
 * Преобразует дату в формате ISO (например, "2025-01-19T08:56:59.556Z") в строку формата "дд.мм.гггг".
 * Примеры:
 * - "2025-01-19T08:56:59.556Z" → "19.01.2025"
 * - "2008-10-22T00:00:00.000Z" → "22.10.2008"
 *
 * @param {string} isoDate - Дата в формате ISO 8601.
 * @returns {string} Дата в формате "дд.мм.гггг".
 */
export const formatDate = (isoDate) => {
  const date = new Date(isoDate);
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();

  return `${day}.${month}.${year}`;
};

/**
 * Функция для определения правильного окончания слова в зависимости от числа.
 * @param {number} count - Число, для которого нужно определить окончание.
 * @returns {string} - Правильное окончание.
 */
export const getReleaseEnding = (count) => {
  const mod10 = count % 10;
  const mod100 = count % 100;

  if (mod10 === 1 && mod100 !== 11) {
    return "выпуск";
  }
  if ([2, 3, 4].includes(mod10) && ![12, 13, 14].includes(mod100)) {
    return "выпуска";
  }
  return "выпусков";
};

/**
 *
 * @param {string} str
 */
export const upperFirst = (str = "") => {
  if (!str) {
    return "";
  }

  return str[0].toUpperCase() + str.slice(1);
};
